export const PAPI_PACKING_SLIP_DOC_TYPE = "packingSlip";
export const PAPI_PACKING_SLIP_NOT_INCLUDED_LABEL = "Not Included";
export const PAPI_PACKING_SLIP_OTHER_SIZE_LABEL = "Other";

// Output format is a render-time choice, independent of which template is
// selected or how that template's `engine` is authored — always offer both.
// See DESIGN_TW-37988_REMOVE_FORMAT_COUPLING.md (D4 / OQ-1).
export const getAvailableFormats = function () {
    return [
        { Name: "Pdf", Value: "pdf" },
        { Name: "Zpl", Value: "zpl" },
    ];
};

/** Trimmed string, or null when absent/blank (TPL-wide / unbound). */
export const normalizePapiTemplateCustomerId = function (customerId) {
    if (customerId == null) return null;
    var s = String(customerId).trim();
    return s ? s : null;
};

/**
 * Distinct WMS customer ids as strings. `"1"` and `1` collapse to one.
 * Empty input → [].
 */
export const distinctWmsCustomerIds = function (wmsCustomerIds) {
    var raw = wmsCustomerIds == null
        ? []
        : (Array.isArray(wmsCustomerIds) ? wmsCustomerIds : [wmsCustomerIds]);
    var seen = {};
    var out = [];
    raw.forEach(function (id) {
        var n = normalizePapiTemplateCustomerId(id);
        if (!n || seen[n]) return;
        seen[n] = true;
        out.push(n);
    });
    return out;
};

/**
 * Unbound (no customerId) = TPL-wide. Bound = visible only when there is
 * exactly one distinct WMS customer and it matches; mixed/empty list = hide bound.
 */
export const isPapiTemplateVisibleForCustomers = function (template, wmsCustomerIds) {
    var bound = normalizePapiTemplateCustomerId(template && template.customerId);
    if (!bound) return true;
    var ids = distinctWmsCustomerIds(wmsCustomerIds);
    return ids.length === 1 && ids[0] === bound;
};

/**
 * @param {Array} documentTemplates
 * @param {boolean} includeDraft
 * @param {number|string|Array} [wmsCustomerIds] - omit/empty/mixed → unbound only (fail closed).
 */
export const filterPapiPackingSlipTemplates = function (documentTemplates, includeDraft, wmsCustomerIds) {
    if (!documentTemplates || !documentTemplates.length) {
        return [];
    }

    // A template's `engine` describes how it was authored, not which output
    // format (PDF/ZPL) it can be rendered as at print time — PAPI supports
    // rendering any packingSlip template as either format. Do not filter by
    // engine here. See DESIGN_TW-37988_REMOVE_FORMAT_COUPLING.md (D1).
    // Unbound templates are TPL-wide; bound customerId must match exactly one
    // WMS customer. Mixed customer lists keep unbound only.
    return documentTemplates.filter(function (template) {
        if (!template || template.docType !== PAPI_PACKING_SLIP_DOC_TYPE) {
            return false;
        }
        if (!includeDraft && template.status && template.status.toLowerCase() !== "published") {
            return false;
        }
        if (!isPapiTemplateVisibleForCustomers(template, wmsCustomerIds)) {
            return false;
        }
        return true;
    });
};

/** Trimmed labelSizeLabel, or Other when absent/blank. */
export const normalizePapiTemplateLabelSizeLabel = function (labelSizeLabel) {
    if (labelSizeLabel == null) return PAPI_PACKING_SLIP_OTHER_SIZE_LABEL;
    var s = String(labelSizeLabel).trim();
    return s ? s : PAPI_PACKING_SLIP_OTHER_SIZE_LABEL;
};

/**
 * Walk mapper output so id lookups ignore optgroup wrappers.
 * Ungrouped rows (Not Included) stay in order.
 */
export const flattenPapiDropdownOptions = function (options) {
    var out = [];
    (options || []).forEach(function (option) {
        if (option && option.isOptGroup && option.options) {
            option.options.forEach(function (child) {
                out.push(child);
            });
        } else if (option) {
            out.push(option);
        }
    });
    return out;
};

/**
 * @param {Array} documentTemplates - published packingSlip templates
 * @param {string|false} [notIncludedLabel] - Leading empty option label.
 *   Pass {@link PAPI_PACKING_SLIP_NOT_INCLUDED_LABEL} for Settings / Pack & Ship.
 *   Pass `false` to omit the empty row (Documents → Packing Slips — Generate always needs a template).
 *   Omit / pass `undefined` / `""` for a blank Name (legacy).
 * Groups by trimmed labelSizeLabel (blank → Other). Group order is first-seen
 * in this list; names inside a group keep catalog order.
 */
export const mapPapiTemplatesToDropdownOptions = function (documentTemplates, notIncludedLabel) {
    const options = [];
    if (notIncludedLabel !== false) {
        options.push({
            Name: notIncludedLabel == null ? "" : notIncludedLabel,
            id: null,
            version: null,
        });
    }
    const groups = [];
    const groupByLabel = {};
    (documentTemplates || []).forEach(function (template) {
        var groupLabel = normalizePapiTemplateLabelSizeLabel(template.labelSizeLabel);
        var group = groupByLabel[groupLabel];
        if (!group) {
            group = {
                isOptGroup: true,
                Name: groupLabel,
                id: null,
                options: [],
            };
            groupByLabel[groupLabel] = group;
            groups.push(group);
        }
        group.options.push({
            Name: template.name,
            id: template.id,
            version: template.version,
            engine: template.engine,
        });
    });
    groups.forEach(function (group) {
        if (group.options.length) {
            options.push(group);
        }
    });
    return options;
};

export const findPapiTemplateVersion = function (options, templateId) {
    if (!templateId || !options) {
        return null;
    }
    const match = flattenPapiDropdownOptions(options).find(function (option) {
        return option.id === templateId;
    });
    return match ? match.version : null;
};

/** True when this customer has Integrated Packing Slips on under PAPI (saved template id). */
export const isPapiPackingSlipsOptedIn = function (templateId) {
    return !!(templateId && String(templateId).trim());
};

/** Read PAPI packing-slip fields from customer Packaging (CustomersSettings API). */
export const readCustomerPapiPackSlipSettings = function (packaging) {
    if (!packaging) {
        return { templateId: null, templateVersion: null, scope: "" };
    }
    return {
        templateId: packaging.SmallParcelPapiPackSlipTemplateId || null,
        templateVersion: packaging.SmallParcelPapiPackSlipTemplateVersion ?? null,
        scope: packaging.SmallParcelPapiPackSlipScope || "",
    };
};

/** True when customer packing-slip layout is Per Package (PAPI scope "package"). Default/empty = Per Order. */
export const isPapiPackageSlipScope = function (scopeOrCustomerOptions) {
    var scope = scopeOrCustomerOptions;
    if (scopeOrCustomerOptions && typeof scopeOrCustomerOptions === "object") {
        scope = scopeOrCustomerOptions.smallParcelPapiPackSlipScope
            || scopeOrCustomerOptions.SmallParcelPapiPackSlipScope
            || "";
    }
    return String(scope || "").toLowerCase() === "package";
};

/** Documents menu: gate only for Per Package when any selected order is not FullyPackageAllocated. */
export const shouldDisablePapiPackingSlipsForSelection = function (usePapi, isPackageScope, anyNotFullyPackageAllocated) {
    return !!(usePapi && isPackageScope && anyNotFullyPackageAllocated);
};

/** Decode ZPL bytes from axios arraybuffer response for printer dispatch. */
export const decodeZplResponseData = function (responseData) {
    if (responseData == null || typeof responseData === "string") {
        return responseData;
    }

    var buffer = null;
    if (responseData instanceof ArrayBuffer) {
        buffer = responseData;
    } else if (responseData.buffer instanceof ArrayBuffer) {
        buffer = responseData.buffer;
    } else if (Object.prototype.toString.call(responseData) === "[object ArrayBuffer]") {
        buffer = responseData;
    }

    if (!buffer) {
        return responseData;
    }

    if (typeof TextDecoder !== "undefined") {
        return new TextDecoder("utf-8").decode(buffer);
    }
    var bytes = new Uint8Array(buffer);
    var result = "";
    for (var i = 0; i < bytes.length; i++) {
        result += String.fromCharCode(bytes[i]);
    }
    return result;
};

/**
 * Pick template from dropdown options.
 * @param {boolean} autoSelectFirst - when true (Documents Generate), if saved is missing /
 *   Not Included / not in list, use the first option that has an id. When false (Pack & Ship),
 *   empty/Not Included saved stays null so the UI can show Not Included.
 */
export const resolvePackingSlipTemplateSelection = function (options, savedDefaultId, autoSelectFirst) {
    const list = flattenPapiDropdownOptions(options);
    const savedInList = savedDefaultId && list.some(function (option) {
        return option.id === savedDefaultId;
    });
    let templateId = savedInList ? savedDefaultId : null;
    if (!templateId && autoSelectFirst) {
        const firstPublished = list.find(function (option) {
            return option && option.id;
        });
        templateId = firstPublished ? firstPublished.id : null;
    }
    return {
        templateId: templateId || null,
        templateVersion: findPapiTemplateVersion(list, templateId),
    };
};

/**
 * Safely extract documentTemplates[] from a raw getCarrierAccountSchema() response value.
 * Handles the case where the API returns either a parsed object or a JSON string.
 */
export const parseDocumentTemplatesFromSchemaResponse = function (schemaData) {
    let schema = schemaData;
    if (typeof schemaData === "string") {
        try {
            schema = JSON.parse(schemaData);
        } catch (_) {
            schema = {};
        }
    }
    return (schema && schema.payload && schema.payload.documentTemplates) || [];
};

/**
 * Decode a WMS ErrorResponse out of an axios error whose request used
 * responseType:'arraybuffer'. Returns the first meaningful descriptor string,
 * or null when the body cannot be parsed.
 */
export const extractPapiErrorMessage = function (error) {
    try {
        const buffer = error && error.response && error.response.data;
        if (!buffer) return null;
        const text = typeof buffer === "string"
            ? buffer
            : decodeZplResponseData(buffer);
        if (!text || typeof text !== "string") return null;
        const parsed = JSON.parse(text);
        const descriptions = parsed && parsed.Descriptions;
        if (!descriptions || !descriptions.length) return null;
        const first = descriptions[0];
        if (first.Hint) return first.Hint;
        if (first.Descriptors && first.Descriptors.length) return first.Descriptors[0];
        return null;
    } catch (_) {
        return null;
    }
};

/**
 * Pack and Print Packing Slip gate: PAPI needs template id; legacy needs SSRS
 * runner + address template. Address template is never required under PAPI.
 */
export const isPackAndPrintPackingSlipConfigured = function (usePapi, isIntegratedPackingSlips, packAdnShipObj) {
    if (!isIntegratedPackingSlips || !packAdnShipObj) {
        return false;
    }
    if (usePapi) {
        return !!(packAdnShipObj.SmallParcelPapiPackSlipTemplateId
            && String(packAdnShipObj.SmallParcelPapiPackSlipTemplateId).trim());
    }
    return !!(packAdnShipObj.SmallParcelPackSlip && packAdnShipObj.SmallParcelAddressTemplate);
};

/** Payload for FindOrders/PapiPackingSlipReportGenerate (Documents + Pack and Print). */
export const buildPapiPackingSlipGeneratePayload = function (opts) {
    opts = opts || {};
    return {
        TransactionIds: opts.transactionIds,
        CustomerId: opts.customerId,
        PapiTemplateId: opts.templateId,
        PapiTemplateVersion: opts.templateVersion,
        OutputFormat: opts.outputFormat || "pdf",
    };
};

/**
 * Fields for showProgressModal after ship. Under PAPI, slips print via
 * documents.combined — do not signal the SSRS packing-slip fallback.
 */
export const buildSsrsPackingSlipsProgressEventFields = function (
    usePapi,
    isIntegratedPackingSlips,
    packAdnShipObj,
    transactionIds,
    customerId
) {
    if (usePapi) {
        return { isIntegratedPackingSlips: false, packingSlipsData: null };
    }
    return {
        isIntegratedPackingSlips: !!isIntegratedPackingSlips,
        packingSlipsData: {
            TemplatedRunner: packAdnShipObj && packAdnShipObj.SmallParcelPackSlip,
            TransactionIds: transactionIds,
            CustomerId: customerId,
            TemplateId: packAdnShipObj && packAdnShipObj.SmallParcelAddressTemplate,
            OutputType: "application/pdf",
        },
    };
};
